import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import type { ConversationEvent, EventFilters } from "../types";
import { MessageCard } from "../components/MessageCard";
import { useAuth } from "../context/AuthContext";

const POLL_INTERVAL = 5000; // 5 seconds

export function Messages() {
  useEffect(() => {
    document.title = "vibe-check ⏵Messages";
  }, []);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<ConversationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const latestEventId = useRef<number | null>(null);
  const [shareTitle, setShareTitle] = useState("");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [shareVisibility, setShareVisibility] = useState<"public" | "users">(
    "public",
  );
  const [grantedUsers, setGrantedUsers] = useState<string[]>([]);
  const [userInput, setUserInput] = useState("");
  const limit = 50;

  // Parse filters from URL
  const filters: EventFilters = useMemo(
    () => ({
      event_uuid: searchParams.get("event_uuid") || undefined,
      session_id: searchParams.get("session_id") || undefined,
      git_remote: searchParams.get("git_remote") || undefined,
      file_name: searchParams.get("file_name") || undefined,
      event_type: searchParams.get("event_type") || undefined,
      date_from: searchParams.get("date_from") || undefined,
      date_to: searchParams.get("date_to") || undefined,
    }),
    [searchParams],
  );

  // Parse sort order from URL
  const sortOrder = (searchParams.get("sort") as "ASC" | "DESC") || "DESC";

  const toggleSortOrder = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sort", sortOrder === "DESC" ? "ASC" : "DESC");
    setSearchParams(newParams);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);
  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined,
  ).length;

  // Update filter helper
  const updateFilter = (key: keyof EventFilters, value: string | undefined) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  // Get unique values for dropdowns
  const uniqueRepos = useMemo(() => {
    const repos = new Set<string>();
    events.forEach((e) => e.git_remote_url && repos.add(e.git_remote_url));
    return Array.from(repos);
  }, [events]);

  const uniqueEventTypes = useMemo(() => {
    const types = new Set<string>();
    events.forEach((e) => e.event_type && types.add(e.event_type));
    return Array.from(types);
  }, [events]);

  // Serialize filters for dependency tracking
  const filtersKey = searchParams.toString();

  // Load events when filters or sort order change
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await api.getEvents(limit, 0, filters, sortOrder);
        setEvents(data.events);
        setOffset(data.events.length);
        setTotal(data.total);
        setError(null);
        // Track the latest event ID for polling
        if (data.events.length > 0) {
          latestEventId.current = data.events[0].id;
        }
        // Scroll to anchored message after load
        if (window.location.hash) {
          setTimeout(() => {
            const el = document.querySelector(window.location.hash);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load messages",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new messages
  useEffect(() => {
    const pollForNewMessages = async () => {
      try {
        const data = await api.getEvents(limit, 0, filters, sortOrder);
        if (data.events.length > 0 && latestEventId.current !== null) {
          // Find new events (those with ID greater than our latest)
          const newEvents = data.events.filter(
            (e) => e.id > latestEventId.current!,
          );
          if (newEvents.length > 0) {
            setEvents((prev) => {
              // Merge new events, avoiding duplicates
              const existingIds = new Set(prev.map((e) => e.id));
              const uniqueNewEvents = newEvents.filter(
                (e) => !existingIds.has(e.id),
              );
              return [...uniqueNewEvents, ...prev];
            });
            setTotal((prev) => prev + newEvents.length);
            latestEventId.current = data.events[0].id;
          }
        }
      } catch {
        // Silently fail on poll errors
      }
    };

    const interval = setInterval(pollForNewMessages, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = async (reset = false) => {
    try {
      setLoading(true);
      const newOffset = reset ? 0 : offset;
      const data = await api.getEvents(limit, newOffset, filters, sortOrder);

      if (reset) {
        setEvents(data.events);
        setOffset(data.events.length);
      } else {
        setEvents((prev) => [...prev, ...data.events]);
        setOffset(newOffset + data.events.length);
      }

      setTotal(data.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const hasMore = offset < total;

  const extractRepoName = (url: string | null) => {
    if (!url) return "local";
    const match = url.match(/\/([^/]+)\.git$/);
    return match ? match[1] : url;
  };

  // Derive share scope from current filters
  const getShareScope = () => {
    if (filters.event_uuid)
      return {
        scope_type: "message" as const,
        scope_event_uuid: filters.event_uuid,
      };
    if (filters.session_id)
      return {
        scope_type: "session" as const,
        scope_session_id: filters.session_id,
      };
    if (filters.git_remote)
      return {
        scope_type: "repository" as const,
        scope_git_remote_url: filters.git_remote,
      };
    if (filters.file_name)
      return {
        scope_type: "conversation" as const,
        scope_file_name: filters.file_name,
      };
    return { scope_type: "all" as const };
  };

  const getShareLabel = () => {
    if (filters.event_uuid)
      return `Share Message ${filters.event_uuid.slice(0, 8)}`;
    if (filters.session_id)
      return `Share Session #${filters.session_id.slice(0, 8)}`;
    if (filters.git_remote)
      return `Share ${extractRepoName(filters.git_remote)}`;
    if (filters.file_name) return `Share ${filters.file_name.split("/").pop()}`;
    return "Share All";
  };

  const handleShare = async () => {
    setShareLoading(true);
    setShareError(null);

    try {
      const scope = getShareScope();
      const data = await api.createShare({
        ...scope,
        visibility: shareVisibility,
        title: shareTitle || undefined,
        granted_users: shareVisibility === "users" ? grantedUsers : undefined,
      });
      setShareUrl(data.share_url);
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : "Failed to create share",
      );
    } finally {
      setShareLoading(false);
    }
  };

  const addGrantedUser = () => {
    const username = userInput.trim().toLowerCase().replace("@", "");
    if (username && !grantedUsers.includes(username)) {
      setGrantedUsers([...grantedUsers, username]);
      setUserInput("");
    }
  };

  const removeGrantedUser = (username: string) => {
    setGrantedUsers(grantedUsers.filter((u) => u !== username));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const openShareModal = () => {
    setShareTitle("");
    setShareUrl(null);
    setShareError(null);
    setShareVisibility("public");
    setGrantedUsers([]);
    setUserInput("");
    setShowShareModal(true);
  };

  if (error && events.length === 0) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => loadEvents(true)}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Messages</h1>
        <div className="flex space-x-2">
          <button
            onClick={openShareModal}
            disabled={events.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <span>{getShareLabel()}</span>
          </button>
          <button
            onClick={() => loadEvents(true)}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filter UI */}
      <div>
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>

          <button
            onClick={toggleSortOrder}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
            title={
              sortOrder === "DESC"
                ? "Showing newest first"
                : "Showing oldest first"
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {sortOrder === "DESC" ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
                />
              )}
            </svg>
            {sortOrder === "DESC" ? "Newest first" : "Oldest first"}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-purple-400 hover:text-purple-300"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.event_uuid && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-600/20 text-pink-300 rounded-full text-sm">
                Message: {filters.event_uuid.slice(0, 8)}
                <button
                  onClick={() => updateFilter("event_uuid", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.session_id && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-600/20 text-purple-300 rounded-full text-sm">
                Session: #{filters.session_id.slice(0, 8)}
                <button
                  onClick={() => updateFilter("session_id", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.git_remote && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">
                Repo: {extractRepoName(filters.git_remote)}
                <button
                  onClick={() => updateFilter("git_remote", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.file_name && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">
                File: {filters.file_name.split("/").pop()}
                <button
                  onClick={() => updateFilter("file_name", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.event_type && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-600/20 text-yellow-300 rounded-full text-sm">
                Type: {filters.event_type}
                <button
                  onClick={() => updateFilter("event_type", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.date_from && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">
                From: {filters.date_from}
                <button
                  onClick={() => updateFilter("date_from", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
            {filters.date_to && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">
                To: {filters.date_to}
                <button
                  onClick={() => updateFilter("date_to", undefined)}
                  className="hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            )}
          </div>
        )}

        {/* Expanded filter panel */}
        {showFilters && (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Session ID
              </label>
              <input
                type="text"
                value={filters.session_id || ""}
                onChange={(e) =>
                  updateFilter("session_id", e.target.value || undefined)
                }
                placeholder="Session ID..."
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Repository
              </label>
              <select
                value={filters.git_remote || ""}
                onChange={(e) =>
                  updateFilter("git_remote", e.target.value || undefined)
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
              >
                <option value="">All repositories</option>
                {uniqueRepos.map((repo) => (
                  <option key={repo} value={repo}>
                    {extractRepoName(repo)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Event Type
              </label>
              <select
                value={filters.event_type || ""}
                onChange={(e) =>
                  updateFilter("event_type", e.target.value || undefined)
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
              >
                <option value="">All types</option>
                {uniqueEventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from || ""}
                onChange={(e) =>
                  updateFilter("date_from", e.target.value || undefined)
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to || ""}
                onChange={(e) =>
                  updateFilter("date_to", e.target.value || undefined)
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* Message count */}
      <div className="text-sm text-gray-400">
        Showing {events.length} of {total} messages
        {hasActiveFilters && " (filtered)"}
      </div>

      <div className="space-y-4 pl-8">
        {events.map((event) => (
          <MessageCard
            key={event.id}
            event={event}
            userAvatar={user?.avatar_url}
            onFilterByUuid={(uuid) => updateFilter("event_uuid", uuid)}
            onFilterBySession={(sessionId) =>
              updateFilter("session_id", sessionId)
            }
            onFilterByRepo={(repoUrl) => updateFilter("git_remote", repoUrl)}
          />
        ))}

        {events.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-400">No messages found</p>
            {hasActiveFilters && (
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your filters or{" "}
                <button
                  onClick={clearFilters}
                  className="text-purple-400 hover:underline"
                >
                  clear all filters
                </button>
              </p>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        )}

        {hasMore && !loading && events.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={() => loadEvents()}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Share Modal - Simplified */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">
                {getShareLabel()}
              </h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {!shareUrl ? (
              <>
                {/* Scope Preview */}
                <div className="mb-4 p-3 bg-gray-700 rounded">
                  <p className="text-sm text-gray-400 mb-1">Sharing:</p>
                  <p className="text-white">
                    {filters.event_uuid &&
                      `Message ${filters.event_uuid.slice(0, 8)}`}
                    {!filters.event_uuid &&
                      filters.session_id &&
                      `Session #${filters.session_id.slice(0, 8)}`}
                    {!filters.event_uuid &&
                      filters.git_remote &&
                      `Repository: ${extractRepoName(filters.git_remote)}`}
                    {!filters.event_uuid &&
                      filters.file_name &&
                      `File: ${filters.file_name.split("/").pop()}`}
                    {!hasActiveFilters && "All your messages"}
                  </p>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    value={shareTitle}
                    onChange={(e) => setShareTitle(e.target.value)}
                    placeholder="My shared conversation"
                    className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                {/* Visibility */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">
                    Who can view
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 p-2 bg-gray-700 rounded cursor-pointer hover:bg-gray-600">
                      <input
                        type="radio"
                        name="visibility"
                        checked={shareVisibility === "public"}
                        onChange={() => setShareVisibility("public")}
                        className="text-purple-600"
                      />
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <div className="text-white text-sm">Public</div>
                          <div className="text-xs text-gray-400">
                            Anyone with the link can view
                          </div>
                        </div>
                      </div>
                    </label>
                    <label className="flex items-center space-x-3 p-2 bg-gray-700 rounded cursor-pointer hover:bg-gray-600">
                      <input
                        type="radio"
                        name="visibility"
                        checked={shareVisibility === "users"}
                        onChange={() => setShareVisibility("users")}
                        className="text-purple-600"
                      />
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        <div>
                          <div className="text-white text-sm">
                            Specific Users
                          </div>
                          <div className="text-xs text-gray-400">
                            Choose who can view
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* User picker (shown when visibility = 'users') */}
                {shareVisibility === "users" && (
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">
                      Grant access to
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), addGrantedUser())
                        }
                        placeholder="Enter username..."
                        className="flex-1 bg-gray-700 text-white rounded px-3 py-2 border border-gray-600 focus:border-purple-500 focus:outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={addGrantedUser}
                        className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 text-sm"
                      >
                        Add
                      </button>
                    </div>
                    {grantedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {grantedUsers.map((username) => (
                          <span
                            key={username}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 text-purple-300 rounded text-sm"
                          >
                            @{username}
                            <button
                              onClick={() => removeGrantedUser(username)}
                              className="hover:text-white"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {grantedUsers.length === 0 && (
                      <p className="text-xs text-gray-500">
                        Add at least one user to share with
                      </p>
                    )}
                  </div>
                )}

                {shareError && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded text-red-400 text-sm">
                    {shareError}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={
                      shareLoading ||
                      (shareVisibility === "users" && grantedUsers.length === 0)
                    }
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                  >
                    {shareLoading ? "Creating..." : "Create Share Link"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Success State */}
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-white mb-4">Share link created!</p>
                  <div className="flex items-center space-x-2 bg-gray-700 rounded p-3 mb-4">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 bg-transparent text-gray-300 text-sm focus:outline-none"
                    />
                    <button
                      onClick={() => copyToClipboard(shareUrl)}
                      className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                    >
                      Copy
                    </button>
                  </div>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
